import { useMemo } from 'react';

import {
  type PropertySchemaMap,
  usePropertySchema,
} from '@/lib/crm/hooks/usePropertySchema';

import type { AhoyPropertyOption, SalesforceObjectType } from '../types';

export function useSalesforceAhoyProperties(
  orgId: number | string | undefined
) {
  const { schema: account } = usePropertySchema(orgId ?? '', 'ahoy_company');
  const { schema: contact } = usePropertySchema(orgId ?? '', 'ahoy_contact');
  const { schema: opportunity } = usePropertySchema(orgId ?? '', 'ahoy_deal');

  return useMemo((): Record<SalesforceObjectType, AhoyPropertyOption[]> => {
    const toOptions = (schema: PropertySchemaMap): AhoyPropertyOption[] =>
      Object.values(schema ?? {})
        // A mapped property must be both create-writable and update-writable:
        // sync applies inbound/outbound updates, which are rejected when
        // value_update_access is 'none' (create-only properties).
        .filter(
          p => p.value_write_access === 'all' && p.value_update_access === 'all'
        )
        .map(p => ({ name: p.property_type_name, label: p.label }))
        .sort((a, b) => a.label.localeCompare(b.label));

    return {
      account: toOptions(account),
      contact: toOptions(contact),
      opportunity: toOptions(opportunity),
    };
  }, [account, contact, opportunity]);
}
